import type { ReactNode } from 'react'

import { cn } from './cn'

/**
 * 一屏的外框与页头。
 *
 * 页面 padding 是 `34px 40px 60px`、内容最宽 1240px——设计稿里每一屏都是这个数，
 * 所以放在这儿一次写对，而不是每屏抄一遍抄歪一个。
 */
const widths = {
  wide: 'max-w-[1240px]',
  /** AI 工作台在超宽屏上由内部网格自行增加列，避免固定 1680px 后留下大片空白。 */
  workspace: 'w-full max-w-none',
  narrow: 'max-w-[1100px]',
  /** 单栏读的那些屏。时间线是一条竖轴，拉到 1240 会让每条卡片长得没法读。 */
  reading: 'max-w-[1000px]',
} as const

export function Page({
  children,
  className,
  /** 工作台使用全宽响应式网格；表单与时间线继续使用较窄档位。 */
  width = 'wide',
}: {
  children: ReactNode
  className?: string
  width?: keyof typeof widths
}) {
  return (
    <div
      className={cn(
        'px-npc-page-x pb-npc-page-bottom pt-npc-page-top',
        'max-[600px]:w-full max-[600px]:max-w-none max-[600px]:px-npc-grid-sm',
        'max-[600px]:pb-npc-card-lg max-[600px]:pt-npc-card-sm',
        widths[width],
        className,
      )}
    >
      {children}
    </div>
  )
}

/**
 * 页头：左边标题与一句说明，右边动作按钮。
 *
 * 标题字号有两档：总览是 30px，别的屏是 28px。设计稿就是这么分的，
 * 因为总览是每天第一眼看的那一屏。
 */
export function PageHeader({
  title,
  subtitle,
  actions,
  breadcrumb,
  size = 'sm',
  align = 'end',
  className,
}: {
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  breadcrumb?: ReactNode
  size?: 'lg' | 'sm'
  /** 机器详情的标题下面还有一行元信息，按钮要对齐到顶。 */
  align?: 'end' | 'start'
  className?: string
}) {
  return (
    <div className={className}>
      {breadcrumb ? (
        <div className="mb-npc-gap-sm text-npc-caption text-npc-ink-softer">{breadcrumb}</div>
      ) : null}
      <div
        className={cn(
          'flex justify-between gap-npc-modal',
          align === 'end' ? 'items-end' : 'items-start',
          'max-[600px]:flex-col max-[600px]:items-stretch max-[600px]:gap-npc-grid-sm',
        )}
      >
        <div className="min-w-0">
          <h1
            className={cn(
              'm-0 font-npc-display font-semibold tracking-npc-tight',
              size === 'lg' ? 'text-npc-h1' : 'text-npc-h1-sm',
            )}
          >
            {title}
          </h1>
          {subtitle ? (
            <div className="mt-npc-gap-xs text-npc-body-lg text-npc-ink-soft">{subtitle}</div>
          ) : null}
        </div>
        {actions ? <div className="flex shrink-0 gap-npc-gap-sm">{actions}</div> : null}
      </div>
    </div>
  )
}

/** 区块小标题：13px / 600，卡里那一行。 */
export function SectionTitle({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('text-npc-section-sm font-semibold', className)}>{children}</div>
}
